import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { useParams } from 'react-router-dom';
import moment from 'moment';
import { Button, Typography } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
// API
import { GET_MY_INVOICES } from 'api/brandSubscriptions/queries';
// Types
import {
  GetMyInvoices,
  GetMyInvoices_getMyInvoices_entities,
  GetMyInvoicesVariables,
} from 'api/brandSubscriptions/types/GetMyInvoices';
import {
  GetMyInvoicesOrderBy,
  SortDirection,
  InvoiceStatus,
  InvoiceType,
} from 'api/graphql-global-types';
// Components
import Table, { Column, SortedInfo, TableFilter } from 'ui/Table';
import BillingDetailsModal from './components/BillingDetailsModal/BillingDetailsModal';
// Helpers
import {
  INVOICE_STATUSES,
  INVOICE_TYPES,
  mapInvoiceStatusToLabel,
  mapInvoiceTypeToLabel,
  isInvoiceDisabled,
} from 'helpers/invoice';
// Constants
import { onlyDateTimeFormat } from 'constants/global';
// Styles
import styles from './BillingTable.module.scss';

const { Title } = Typography;

const BillingTable: React.FC = () => {
  const { ownerId } = useParams<{ ownerId: string }>();
  const [pageSize, setPageSize] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [sort, setSortInfo] = useState<SortedInfo<GetMyInvoicesOrderBy>>({
    order: SortDirection.DESC,
    key: GetMyInvoicesOrderBy.createdAt,
  });
  const [searchFilters, setSearchFilters] = useState<TableFilter[]>([]);
  const [selectedInvoice, setSelectedInvoice] =
    useState<GetMyInvoices_getMyInvoices_entities | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const getInvoicesInput = (): { input: any } => {
    const filterStatuses = searchFilters?.find((item) => item.key === 'status')
      ?.value as InvoiceStatus[] | undefined;
    const filterTypes = searchFilters?.find((item) => item.key === 'type')
      ?.value as InvoiceType[] | undefined;

    const input: any = {
      ownerId,
      limit: pageSize,
      offset: (currentPage > 1 ? currentPage - 1 : 0) * pageSize,
      orderBy: sort.key,
      direction: sort.order,
      ...(filterStatuses && filterStatuses.length > 0
        ? { statuses: filterStatuses }
        : {}),
      ...(filterTypes && filterTypes.length > 0 ? { types: filterTypes } : {}),
    };

    return { input };
  };

  const { data, loading } = useQuery<GetMyInvoices, GetMyInvoicesVariables>(
    GET_MY_INVOICES,
    {
      variables: getInvoicesInput(),
      fetchPolicy: 'cache-and-network',
      skip: !ownerId,
    }
  );

  const handleDownload = (invoicePdfUrl: string | null): void => {
    if (invoicePdfUrl) {
      window.open(invoicePdfUrl, '_blank');
    }
  };

  const handleCloseModal = (): void => {
    setIsModalOpen(false);
    setSelectedInvoice(null);
  };

  const handleOpenModal = (
    invoice: GetMyInvoices_getMyInvoices_entities
  ): void => {
    setSelectedInvoice(invoice);
    setIsModalOpen(true);
  };

  const handleDownloadInvoice = (): void => {
    if (selectedInvoice?.invoicePdfUrl) {
      window.open(selectedInvoice.invoicePdfUrl, '_blank');
    }
  };

  const columns: Column<
    GetMyInvoices_getMyInvoices_entities,
    GetMyInvoicesOrderBy
  >[] = [
    {
      title: 'ID',
      dataIndex: 'id',
      sorterType: 'text',
    },
    {
      title: 'Date',
      dataIndex: 'paidAt',
      key: 'paidAt',
      render: (paidAt: string | null) => (
        <span>
          {paidAt ? moment(paidAt).format(onlyDateTimeFormat) : 'N/A'}
        </span>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      withCheckboxFilters: true,
      filters: INVOICE_STATUSES.map((item) => ({
        text: item.label,
        value: item.value,
      })),
      render: (status: InvoiceStatus) => (
        <span>{mapInvoiceStatusToLabel(status)}</span>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      withCheckboxFilters: true,
      filters: INVOICE_TYPES.map((item) => ({
        text: item.label,
        value: item.value,
      })),
      render: (type: InvoiceType) => <span>{mapInvoiceTypeToLabel(type)}</span>,
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: GetMyInvoicesOrderBy.total,
      sorterType: 'number',
      render: (total: number) => <span>${total.toFixed(2)}</span>,
    },
    {
      title: 'Download Invoice',
      dataIndex: 'invoicePdfUrl',
      key: 'invoicePdfUrl',
      render: (_: string, record: GetMyInvoices_getMyInvoices_entities) => (
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          onClick={() => handleDownload(record.invoicePdfUrl)}
          disabled={!record.invoicePdfUrl}
        >
          Download
        </Button>
      ),
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      key: 'actions',
      render: (_: string, record: GetMyInvoices_getMyInvoices_entities) => (
        <Button
          type="default"
          onClick={() => handleOpenModal(record)}
          disabled={isInvoiceDisabled(record.status)}
        >
          View Details
        </Button>
      ),
    },
  ];

  return (
    <div className={styles.root}>
      <Title level={2}>Billing</Title>
      <div className={styles.tableContainer}>
        <Table
          loading={loading}
          columns={columns}
          data={data?.getMyInvoices.entities}
          total={data?.getMyInvoices.total}
          defaultPageSize={pageSize}
          defaultCurrent={currentPage}
          setPageSize={setPageSize}
          setCurrentPage={setCurrentPage}
          searchFilters={searchFilters}
          setSearchFilters={setSearchFilters}
          sortInfo={sort}
          setSortInfo={setSortInfo}
        />
      </div>

      <BillingDetailsModal
        open={isModalOpen}
        invoice={selectedInvoice}
        onClose={handleCloseModal}
        onDownloadInvoice={handleDownloadInvoice}
      />
    </div>
  );
};

export default BillingTable;
